import { useCallback, useEffect, useState } from "react";

const KEY = "trex.read-theme";

function resolveInitial() {
  if (typeof window === "undefined") return "light";
  try {
    const stored = window.localStorage.getItem(KEY);
    if (stored === "light" || stored === "dark") return stored;
  } catch {
    /* ignore — private mode etc. */
  }
  // No stored preference → respect the OS. Fallback is LIGHT (per brand spec
  // for long-form reading).
  try {
    if (window.matchMedia && window.matchMedia("(prefers-color-scheme: dark)").matches) {
      return "dark";
    }
  } catch { /* ignore */ }
  return "light";
}

/**
 * Reader theme — shared across /articles/:slug and /docs/*.
 * Returns: [theme, setTheme, toggle] where theme is "light" | "dark".
 *
 * - Persists to localStorage.
 * - Subscribes to OS preference changes ONLY when no user choice is stored.
 * - Listens for cross-tab `storage` events so two tabs stay in sync.
 */
export function useReadTheme() {
  const [theme, setTheme] = useState(resolveInitial);

  // Persist when the user changes it
  useEffect(() => {
    try { window.localStorage.setItem(KEY, theme); } catch { /* ignore */ }
  }, [theme]);

  // React to OS changes only when no explicit user choice
  useEffect(() => {
    let mql;
    try { mql = window.matchMedia("(prefers-color-scheme: dark)"); } catch { return; }
    const onChange = (e) => {
      try {
        if (!window.localStorage.getItem(KEY)) {
          setTheme(e.matches ? "dark" : "light");
        }
      } catch { /* ignore */ }
    };
    mql.addEventListener?.("change", onChange);
    return () => mql.removeEventListener?.("change", onChange);
  }, []);

  // Cross-tab sync
  useEffect(() => {
    const onStorage = (e) => {
      if (e.key === KEY && (e.newValue === "light" || e.newValue === "dark")) {
        setTheme(e.newValue);
      }
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  const toggle = useCallback(() => {
    setTheme((t) => (t === "dark" ? "light" : "dark"));
  }, []);

  return [theme, setTheme, toggle];
}
